'use client';

import Image from '@components/Image';
import { Carousel as NGGCarousel } from '@ngg/components';
import { SearchIcon } from '@ngg/icons';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dictionary } from 'types/common';
import type { Image as IImage } from 'types/image';
import type { Product, Variant } from 'types/product';

import { FEATURE_FLAGS } from '@/config/featureFlags';
import { cn } from '@/lib/utils';
import { TITLE_SITE_NAME } from '@/utils/const';
import { getProductImageAltText } from '@/utils/getProductImageAltText';

import Fullscreen from './fullscreen';
import { NextButton, PrevButton } from './navigation';
import Thumbnails from './thumbnails';
import Video from './video';

export interface CarouselProps {
    dictionary: Dictionary;
    product: Product;
    variant?: Variant;
    productImages: IImage[];
    className?: string;
    brand?: string | null;
    designer?: string | null;
    campaignBadges?: ReactNode;
}

const visibleSlides = 1;

const setSlideItem = (
    entry: IntersectionObserverEntry,
    setActiveIndex: (index: number) => void,
) => {
    const target = entry.target as HTMLElement;

    if (entry.intersectionRatio > 0.5) {
        setActiveIndex(Number(target.dataset.index));
    }
};

/**
 * @deprecated - Deprecated in favor of the new ProductPageGallery.tsx component
 */
export default function Carousel({
    variant,
    product,
    dictionary,
    productImages,
    className,
    brand,
    designer,
    campaignBadges,
}: CarouselProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        if (!ref.current) return undefined;

        const observer = new IntersectionObserver(
            ([entry]) => setSlideItem(entry, setActiveIndex),
            {
                root: ref.current,
                rootMargin: '0px',
                threshold: [0.1, 0.5, 0.9],
            },
        );

        Array.from(ref.current.children).forEach((child) => {
            observer.observe(child);
        });

        return () => observer.disconnect();
    }, [ref]);

    const nextButtonVisibility = useCallback(() => {
        if (!ref.current) return true;
        return activeIndex < ref.current.children.length - visibleSlides;
    }, [ref, activeIndex]);

    const goToItem = useCallback(
        (index: number, smooth = true) => {
            if (index < 0) return;
            if (ref.current && index === ref.current.children.length) return;

            const currentChild = ref.current?.children[index] as HTMLDivElement;
            ref?.current?.scrollTo({
                left: currentChild?.offsetLeft,
                behavior: smooth ? 'smooth' : 'auto',
            });
            setActiveIndex(index);
        },
        [ref, setActiveIndex],
    );

    useEffect(() => {
        if (variant) goToItem(0, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variant]);

    return (
        <>
            <Fullscreen
                open={isFullscreen}
                active={activeIndex}
                onClose={() => setIsFullscreen(false)}
                images={productImages}
                goToCarouselItem={goToItem}
            />
            <div
                className={cn(
                    'items-start overflow-hidden md:relative md:flex md:flex-col lg:flex-row',
                    className,
                )}>
                {campaignBadges}
                <SearchIcon className="pointer-events-none absolute right-[8%] top-3 z-10 h-5 w-5 text-black hover:cursor-zoom-in lg:right-4 lg:top-4 lg:h-6 lg:w-6" />
                <Thumbnails
                    activeIndex={activeIndex}
                    goToItem={goToItem}
                    images={productImages}
                    className="z-20 hidden max-h-full max-w-full flex-shrink-0 bg-white/80 p-2 backdrop-blur-md max-lg:mt-1 md:left-0 md:top-0 md:flex md:overflow-auto lg:flex-col"
                />
                <NGGCarousel
                    ref={ref}
                    PreviousButton={() =>
                        PrevButton({
                            isVisible: activeIndex > 0,
                            onClick: (e) => {
                                goToItem(activeIndex - 1, false);
                                if (activeIndex - 1 <= 0) {
                                    e.currentTarget.blur();
                                }
                            },
                        })
                    }
                    NextButton={() =>
                        NextButton({
                            isVisible: nextButtonVisibility(),
                            onClick: (e) => {
                                goToItem(activeIndex + 1, false);
                                if (
                                    ref?.current &&
                                    activeIndex + 1 >=
                                        ref.current.children.length -
                                            visibleSlides
                                ) {
                                    e.currentTarget.blur();
                                }
                            },
                        })
                    }
                    classNames={{
                        wrapper:
                            '[--gap:0px] max-lg:[--slides-per-view:1.05] md:-order-1 lg:-order-none w-full',
                        slider: 'scrollbar-none h-full',
                        item: 'bg-grey-100 aspect-square',
                    }}>
                    {/* TODO: Account for environment images of a product. These are not square */}
                    {productImages?.map((media, index) => (
                        <div
                            key={media.id}
                            className="relative flex h-full justify-center">
                            {/* ONLY IMAGES FOR NOW, ADD SUPPORT FOR OTHER MEDIA TYPES LATER */}
                            {media.type === 'image' && (
                                <Image
                                    src={media.thumbnail ?? media.src}
                                    alt={getProductImageAltText(
                                        variant?.title ?? product?.title,
                                        brand,
                                        designer,
                                        dictionary.titleSiteName ??
                                            TITLE_SITE_NAME,
                                    )}
                                    width={1000}
                                    height={1000}
                                    className="h-full w-full cursor-zoom-in object-cover mix-blend-multiply"
                                    onClick={() => {
                                        setIsFullscreen(true);
                                        setActiveIndex(index);
                                    }}
                                    sizes="(max-width: 768px) 100vw, (max-width: 1920px) 40vw, 64em"
                                    widths={[640, 960, 1280, 1920]}
                                    priority
                                />
                            )}
                            {media.type === 'video' &&
                                FEATURE_FLAGS.products.videos && (
                                    <Video src={media.src} />
                                )}
                            {index === 0 ? (
                                <p className="absolute left-2 top-2 mr-2 hidden max-w-full bg-white px-2 py-1 text-xxs text-grey-300 md:bottom-3 md:left-3 md:top-auto md:mr-3 md:block md:px-3 md:text-xs">
                                    {dictionary.imageDisclaimerText}
                                </p>
                            ) : null}
                        </div>
                    ))}
                </NGGCarousel>
            </div>
        </>
    );
}
